import React, { useState, useEffect } from "react";
import { Pencil } from "lucide-react";
import { Button, Card } from "@mui/material";
import { CustomFooterItem } from "../types/footer";
import { Pagination } from "../types/form";
import { getAllGLobalFooters } from "../utils/requests";

interface FooterThemeSelectProps {
    isSuperShopUser: boolean; // Prop to check if user is a super shop user
}

const FooterThemeSelect: React.FC<FooterThemeSelectProps> = ({
    isSuperShopUser,
}) => {
    const [footers, setFooters] = useState<CustomFooterItem[]>([]);
    const [pagination, setPagination] = useState<Pagination | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedFooter, setSelectedFooter] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchFooters();
    }, []);

    const fetchFooters = async (page = 1, perPage = 10) => {
        try {
            setLoading(true);
            const response = await getAllGLobalFooters({
                perPage,
                page,
            });
            setFooters(response.data);
            setPagination(response.page);
            setError(null);
        } catch (err) {
            setError("Error fetching footers. Please try again.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectFooter = (footerId: string) => {
        setSelectedFooter(footerId);
        // Handler left blank as requested
    };

    const handleEditFooter = (footerId: string) => {
        // Edit functionality would go here
        console.log(`Edit footer with ID: ${footerId}`);
    };

    const renderFooterPreview = (footer: CustomFooterItem) => {
        if (footer.images) {
            return (
                <div className="h-32 w-full overflow-hidden rounded-t-md">
                    <img
                        src={footer.images}
                        alt={footer.name}
                        className="w-full h-full object-cover"
                    />
                </div>
            );
        } else if (footer.settings?.content) {
            return (
                <div
                    className="h-32 w-full overflow-hidden rounded-t-md bg-gray-100"
                    dangerouslySetInnerHTML={{
                        __html: footer.settings.content,
                    }}
                />
            );
        } else {
            return (
                <div className="h-32 w-full bg-gray-200 rounded-t-md flex items-center justify-center text-gray-500">
                    No preview available
                </div>
            );
        }
    };

    if (loading && footers.length === 0) {
        return (
            <div className="flex justify-center p-8">Loading footers...</div>
        );
    }

    if (error) {
        return <div className="text-red-500 p-4">{error}</div>;
    }

    return (
        <div className="container mx-auto p-4 min-w-[600px]">
            <h2 className="text-2xl font-bold mb-6">Select Footer Theme</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {footers.map((footer) => (
                    <Card
                        key={footer._id}
                        className={`cursor-pointer transition-all duration-200 hover:shadow-md overflow-hidden ${
                            selectedFooter === footer._id
                                ? "ring-2 ring-blue-500"
                                : ""
                        }`}
                        onClick={() => handleSelectFooter(footer._id)}
                    >
                        {renderFooterPreview(footer)}

                        <div className="p-4">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h3 className="font-semibold truncate">
                                        {footer.name}
                                    </h3>
                                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                        {footer.description || "No description"}
                                    </p>
                                    <div className="flex mt-2 space-x-2">
                                        <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                                            {footer.screenMode}
                                        </span>
                                        {footer.isFrontHeader && (
                                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                                Front
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {isSuperShopUser && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        className="ml-2"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditFooter(footer._id);
                                        }}
                                    >
                                        <Pencil className="h-4 w-4 mr-1" />
                                        Edit
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            {footers.length === 0 && !loading && (
                <div className="text-center py-8">
                    <p className="text-gray-500">No footer themes found</p>
                </div>
            )}

            {pagination && pagination.totalPage > 1 && (
                <div className="flex justify-center mt-6 space-x-2">
                    <Button
                        variant="outlined"
                        disabled={pagination.currentPage <= 1}
                        onClick={() => fetchFooters(pagination.currentPage - 1)}
                    >
                        Previous
                    </Button>

                    <span className="py-2 px-4">
                        Page {pagination.currentPage} of {pagination.totalPage}
                    </span>

                    <Button
                        variant="outlined"
                        disabled={
                            pagination.currentPage >= pagination.totalPage
                        }
                        onClick={() => fetchFooters(pagination.currentPage + 1)}
                    >
                        Next
                    </Button>
                </div>
            )}
        </div>
    );
};

export default FooterThemeSelect;
